// Branded confirm dialog — replaces the native window.confirm()
// blue Chrome alert that breaks visual flow on destructive actions
// (Delete row, Mark as Cancelled bulk op, etc.).
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <ConfirmDialog
//     open={open}
//     title="Delete receipt REC-2026-3012?"
//     message="This will be recorded in the Audit Log Center."
//     confirmLabel="Delete"
//     variant="danger"
//     onCancel={() => setOpen(false)}
//     onConfirm={() => { doDelete(); setOpen(false); }}
//   />
//
// Click on the backdrop or Escape closes via onCancel. The confirm
// button auto-focuses so Enter triggers it directly.
import React, { useEffect, useRef } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export type ConfirmVariant = 'danger' | 'warning' | 'primary';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** Show a discreet audit-log line under the message. */
  auditNote?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const VARIANT_STYLES: Record<ConfirmVariant, {
  iconBg: string;
  iconText: string;
  button: string;
}> = {
  danger: {
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    button: 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm shadow-rose-500/20',
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    button: 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm shadow-amber-500/20',
  },
  primary: {
    iconBg: 'bg-primary-100',
    iconText: 'text-primary-600',
    button: 'bg-primary-600 hover:bg-primary-700 text-white shadow-sm shadow-primary-500/20',
  },
};

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  auditNote,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') confirmRef.current?.click();
    };
    window.addEventListener('keydown', onKey);
    // Focus the confirm button after the modal mounts so keyboard
    // users land on the primary action.
    const t = setTimeout(() => confirmRef.current?.focus(), 30);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open, onCancel]);

  if (!open) return null;
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-[1px]"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden font-poppins animate-fadeInUp">
        {/* Close X */}
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={cn(
              'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center',
              styles.iconBg,
            )}>
              <ExclamationTriangleIcon className={cn('w-6 h-6', styles.iconText)} />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id="confirm-title"
                className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug"
              >
                {title}
              </h3>
              {message && (
                <div className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {message}
                </div>
              )}
              {auditNote && (
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-md px-3 py-2">
                  This action will be recorded in the <strong>Audit Log Center</strong>.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 pt-2 flex justify-end items-center gap-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/40 dark:bg-gray-800/40">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className={cn('px-4 py-2 rounded-lg text-sm font-semibold transition-colors', styles.button)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
